import type { PaymentIntentDataModel } from '@payments/interfaces/paymentIntent.interface';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import { isUserCancellation } from '../../shared/utils/paymentFailure';
import type { PaymentTimes, UseGPayParams } from '@payments/interfaces/gPay';
import { extractWalletCancelMessage, resolveWalletCancelReason } from '../utils/walletCancel';
import { usePaymentError } from './usePaymentError';
import { usePaymentIntentCache } from './usePaymentIntentCache';
import { loadGooglePayScript } from '../shared/utils/googlePay';
import { chargebeeSdk } from '../utils/chargebeeSdk';
import { isPaymentIntentSnapshotValid } from '../utils/paymentIntentSnapshot';
import type { PaymentIntentSnapshot } from '../interfaces/paymentIntentSnapshot.interface';
import { rememberPaymentMethod } from '../utils/paymentMethodStorage';

export function useGPay({
  roundedPrice,
  chargebeeId,
  upsale,
  isDisabled,
  requestPayerEmail,
  onSuccess,
  onToggleLoader,
  onAction,
  onUpdateComponent,
}: UseGPayParams) {
  const { $paymentMethods, $logger } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const API = useKegelApi();
  const store = useKegelAnalyticStore();
  const dialogStore = useDialogStore();
  const { openError } = useAllModals();
  const { roundNumber, gatewayType } = useHelpers();
  const { track } = useEventsDispatcher();
  const { initUser } = useEmail();
  const startTime = ref<number>(0);
  const contentLoading = ref(false);
  const buttonWasMounted = ref(false);
  const paymentIntentSnapshot = ref<PaymentIntentSnapshot | null>(null);
  const clickedIntentSnapshot = ref<PaymentIntentSnapshot | null>(null);
  const { buyUpsale } = payments();
  const intentCache = usePaymentIntentCache('google_pay');

  function gPayClicked() {
    if (!contentLoading.value || isDisabled.value) {
      return;
    }
    clickedIntentSnapshot.value = paymentIntentSnapshot.value;
    startTime.value = performance.now();
    onToggleLoader();
    track('Check-out Started', {
      type: $paymentMethods?.googlePay,
      gateway: gatewayType.value,
    });
  }

  async function handlePayment(
    component: any,
    paymentTimes: PaymentTimes,
    mountedSnapshot: PaymentIntentSnapshot,
  ) {
    buttonWasMounted.value = true;

    const handleCancel = (payload?: unknown) => {
      const timeToCloseMs = startTime.value ? Math.round(performance.now() - startTime.value) : 0;
      const reason = resolveWalletCancelReason(payload);

      try {
        track('googlepay_syscheeckout_closed', { reason, time_to_close_ms: timeToCloseMs });

        if (reason === 'error') {
          reportPaymentError({
            error: { message: extractWalletCancelMessage(payload) } as ApiErrorModel,
            errorCode: 'Google pay Wallet Rejected',
            stage: upsale.value ? 'Upsale' : 'Subscription',
            type: $paymentMethods.googlePay,
          });

          track('Payment duration', {
            type: $paymentMethods.googlePay,
            gateway: gatewayType.value,
            total_duration: roundNumber(timeToCloseMs / 1000),
            result: 'error',
          });
        }
      } finally {
        onToggleLoader();
        contentLoading.value = false;
        store.isDisabledBaseDialog = false;
        buttonWasMounted.value = false;
        onUpdateComponent();
      }
    };

    contentLoading.value = true;
    const { paymentIntent } = await component.handlePayment({ cancel: handleCancel }).then((result: any) => {
      paymentTimes.authorizationEndTime = (performance.now() - startTime.value) / 1000;
      track('Authorization_Start', {
        type: $paymentMethods.googlePay,
        gateway: gatewayType.value,
      });
      return result;
    }).catch((error: unknown) => {
      throw (error);
    });

    const paidSnapshot = resolvePaidSnapshot(mountedSnapshot);

    if (!isPaymentIntentSnapshotValid(paidSnapshot, { amount: roundedPrice.value, planCode: chargebeeId.value })) {
      contentLoading.value = false;
      dialogStore.setLoader(false);
      onToggleLoader();
      store.isDisabledBaseDialog = false;
      buttonWasMounted.value = false;
      intentCache.drop(paidSnapshot.amount);
      clearPaymentIntent();
      onUpdateComponent();
      return;
    }

    const payerEmail = paymentIntent.payer_info?.customer?.email;
    const result = await authorizeUser(payerEmail);

    if (!result && requestPayerEmail.value) {
      openError({ content: 'lp_v7_pop_up_payment_error_message' });
      onToggleLoader();
      contentLoading.value = false;
      dialogStore.setLoader(false);
      intentCache.drop(paidSnapshot.amount);

      $logger.note({
        category: 'payment',
        message: payerEmail ? 'Google Pay user authorization failed' : 'Google Pay returned no email address',
        level: 'warning',
      });

      return;
    }

    const { error, paymentAcceptStartTime } = await acceptPayment(paymentIntent.id as number);
    paymentTimes.paymentAcceptEndTime = (performance.now() - paymentAcceptStartTime) / 1000;
    if (error) {
      throw (error);
    } else {
      intentCache.drop(paidSnapshot.amount);
      track('Authorization_Success', {
        type: $paymentMethods.googlePay,
        price: roundedPrice.value,
      });
      onSuccess(paymentIntent.id);
    }
    rememberPaymentMethod('google-pay');
    trackGooglePayDuration(paymentTimes, 'success');
  }

  async function acceptPayment(paymentIntentId: string) {
    const acceptPayload = {
      intent_id: paymentIntentId,
      item: chargebeeId.value,
      apple_pay: true,
      web_user_uuid: store.userUuid,
      new_flow: true,
    };
    dialogStore.setLoader(true);
    store.isDisabledBaseDialog = true;

    const paymentAcceptStartTime = performance.now();
    let error;
    if (upsale.value) {
      error = await buyUpsale({
        intent_id: paymentIntentId,
        item: chargebeeId.value,
        web_user_uuid: store.userUuid,
        stage: 'upsale',
        amount: roundedPrice.value,
      });
    } else {
      error = await API.creditCardAccept(acceptPayload);
    }

    return { error, paymentAcceptStartTime };
  }

  function trackGooglePayDuration(paymentTimes: PaymentTimes, result: 'success' | 'error') {
    const finalTime = (performance.now() - startTime.value) / 1000;

    track('Payment duration', {
      type: $paymentMethods.googlePay,
      gateway: gatewayType.value,
      authorization_duration: roundNumber(paymentTimes.authorizationEndTime),
      backend_payment_duration: roundNumber(paymentTimes.paymentAcceptEndTime),
      total_duration: roundNumber(finalTime),
      result,
    });
  }

  function handleGooglePayFailure(error: ApiErrorModel, paymentTimes: PaymentTimes, amount: number) {
    contentLoading.value = false;
    dialogStore.setLoader(false);
    onToggleLoader();
    store.isDisabledBaseDialog = false;

    const wasCancelledByUser = isUserCancellation(error);

    if (!wasCancelledByUser) {
      intentCache.drop(amount);
    }

    const failure = {
      error,
      errorCode: 'Google pay Error',
      stage: upsale.value ? 'Upsale' : 'Subscription',
      type: $paymentMethods.googlePay,
    };

    reportPaymentError(failure);

    buttonWasMounted.value = false;
    onUpdateComponent();

    if (!wasCancelledByUser) {
      trackGooglePayDuration(paymentTimes, error?.status === 409 ? 'success' : 'error');
    }

    if (error?.status === 409) {
      onAction();
      return;
    }

    if (!wasCancelledByUser) {
      openError({ content: 'lp_v7_pop_up_payment_error_message' });
    }
  }

  async function mountGooglePayButton(
    paymentIntentData: PaymentIntentDataModel,
    amount: number,
    isCurrent: () => boolean = () => true,
  ): Promise<void> {
    await loadGooglePayScript();

    const component = await chargebeeSdk().getInstance().load('google-pay');

    if (!isCurrent()) return;

    const mountedSnapshot: PaymentIntentSnapshot = { amount, planCode: chargebeeId.value };

    component.setPaymentIntent(paymentIntentData);
    paymentIntentSnapshot.value = mountedSnapshot;

    if (buttonWasMounted.value) return;

    contentLoading.value = true;
    document.getElementById('google-pay')?.replaceChildren();
    await component.mountPaymentButton('#google-pay', {
      buttonColor: '#111113',
      buttonType: 'pay',
      buttonSizeMode: 'fill',
      buttonLocale: 'en',
    }, { requestPayerEmail: requestPayerEmail.value });

    if (!isCurrent()) return;

    void awaitGooglePayPayment(component, mountedSnapshot);
  }

  async function awaitGooglePayPayment(
    component: any,
    mountedSnapshot: PaymentIntentSnapshot,
  ): Promise<void> {
    clickedIntentSnapshot.value = null;
    const paymentTimes: PaymentTimes = {
      authorizationEndTime: null,
      paymentAcceptEndTime: null,
    };
    try {
      await handlePayment(component, paymentTimes, mountedSnapshot);
    } catch (error) {
      handleGooglePayFailure(error as ApiErrorModel, paymentTimes, resolvePaidSnapshot(mountedSnapshot).amount);
    }
  }

  async function authorizeUser(email: string): Promise<boolean> {
    if (!requestPayerEmail.value) {
      return true;
    }

    if (!email) {
      sendEmailRetrievedEvent(false);
      return false;
    }

    const result = await initUser(false, email);
    sendEmailRetrievedEvent(result);

    return result;
  }

  function sendEmailRetrievedEvent(emailAttached: boolean): void {
    track('Payment Email Retrieved', {
      payment_method: $paymentMethods?.googlePay,
      email_attached: emailAttached,
    });
  }

  function resolvePaidSnapshot(mountedSnapshot: PaymentIntentSnapshot): PaymentIntentSnapshot {
    return clickedIntentSnapshot.value ?? mountedSnapshot;
  }

  function clearPaymentIntent(): void {
    paymentIntentSnapshot.value = null;
  }

  return {
    gPayClicked,
    contentLoading,
    mountGooglePayButton,
    clearPaymentIntent,
  };
}
